// "Match all of: [field] [is] [value]", as many rows as you like, producing the
// conditions JSON underneath.
//
// This component holds NO field name, NO operator name and NO enum value. Every
// list — the fields, each field's operators, each operator's arity, the closed
// value lists, and even which endpoint a live list comes from and which column of
// it to store — arrives in `vocabulary`, served on the `saved-views` descriptor and
// projected from src/views/registry.js's own `FIELDS`. All the decisions live in
// ../../pages/admin/view-conditions.js and are tested under node --test; what is
// left here is markup and one fetch per live list.
//
// THE LIVE LISTS ARE HTTP READS AND ONE RULE DECIDES EVERY CASE: a row's stored
// value is never cleared because a list did not arrive. While loading, a closed
// control is disabled and still shows the stored value. If the read fails, the
// stored value is still rendered and Save is NOT blocked — one flaky GET must not
// make a view uneditable when the server validates every value regardless.

import { useEffect, useMemo, useRef, useState } from 'react';

import { apiFetch } from '../../api/client.js';
import {
  addRow, asList, authorableFields, canAddConditionRow, conditionCount, conditionProblems,
  conditionsToDraft,
  draftToConditions, fieldLabel, humanize, maxConditionRows, newConditionRow, refieldRow,
  removeRow, reoperateRow, replaceRow, setRowValue, sourceOptions, specFor, valueControlFor,
  withStoredOptions,
} from '../../pages/admin/view-conditions.js';

/** The heading for a group, from its own key. `all` and `any` are the compiler's
 *  two keys, not a vocabulary — but the sentence is still built from the key so
 *  there is one place it is written. */
const groupHeading = (group) => `Match ${group} of these`;

/**
 * Each live list, fetched once per screen and keyed by source so a failed tag read
 * does not degrade the queue dropdown. Only the sources the current rows actually
 * need are fetched.
 */
function useValueSources(needed) {
  const [sources, setSources] = useState({});
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify(needed);

  useEffect(() => {
    const wanted = JSON.parse(key);
    if (wanted.length === 0) return undefined;
    const controller = new AbortController();
    setSources((prev) => ({
      ...prev,
      ...Object.fromEntries(wanted
        .filter((s) => prev[s.source]?.status !== 'ready')
        .map((s) => [s.source, { status: 'loading', rows: [] }])),
    }));

    for (const spec of wanted) {
      // The source name IS the path segment — /users, /queues, /ticket-statuses,
      // /ticket-forms, /tags, /custom-fields are six of the seven read endpoints
      // in src/routes/api/config.js — so there is no table to keep in step here.
      // `include_inactive` is asked for only where the endpoint takes it, because
      // `validateQuery` answers 400 for an unrecognised parameter.
      const path = `/${spec.source}${spec.includeInactive ? '?include_inactive=true' : ''}`;
      apiFetch(path, { signal: controller.signal })
        .then((res) => setSources((prev) => ({
          ...prev, [spec.source]: { status: 'ready', rows: res.data ?? [] },
        })))
        .catch((err) => {
          if (err?.name === 'AbortError') return;
          setSources((prev) => ({ ...prev, [spec.source]: { status: 'failed', rows: [] } }));
        });
    }
    return () => controller.abort();
  }, [key, attempt]);

  return [sources, () => setAttempt((n) => n + 1)];
}

function CheckboxList({ id, options, selected, disabled, onToggle }) {
  if (options.length === 0) {
    return <p className="text-xs text-ink-muted">Nothing to choose from yet.</p>;
  }
  return (
    <div id={id} className="grid gap-1 rounded border border-line bg-surface p-2 sm:grid-cols-2">
      {options.map((option) => (
        <label key={option.value} className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="size-4 rounded accent-brand-600 focus-visible:outline-2
                       focus-visible:outline-offset-2 focus-visible:outline-brand-600
                       dark:focus-visible:outline-brand-400"
            checked={selected.includes(option.value)}
            disabled={disabled}
            onChange={() => onToggle(option.value)}
          />
          {option.label}
        </label>
      ))}
    </div>
  );
}

/**
 * The tag box keeps its own TEXT while it is being typed in.
 *
 * Derived straight from the committed array it would be untypeable: a value of
 * `names.join(' ')` swallows the trailing space the instant it is typed, so a
 * second tag name can never be started. The committed value is still updated on
 * every keystroke; only the rendering of the half-typed text is local, and the
 * row's positional id as a React key is what reseeds it when the row changes.
 */
function TagsInput({ initial, options, listId, onChange }) {
  const [text, setText] = useState(initial);
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <input
        type="text"
        list={listId}
        className="field-input min-w-0 flex-1 py-1 text-sm"
        placeholder="tag names, separated by spaces"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          onChange(event.target.value.split(/\s+/).filter(Boolean));
        }}
      />
      <datalist id={listId}>
        {options.map((option) => <option key={option.value} value={option.value} />)}
      </datalist>
    </span>
  );
}

function ValueControl({ row, control, sources, listId, onChange }) {
  const loaded = control.source ? sources[control.source] : null;
  const status = control.source ? (loaded?.status ?? 'loading') : 'ready';
  const fetched = control.source ? sourceOptions(loaded?.rows, control) : control.options;

  if (control.kind === 'none') {
    return <span className="text-sm text-ink-muted">Nothing else to say</span>;
  }

  if (control.kind === 'hours') {
    // `min="1"`, not `min="0"`. Zero is not a bound at either polarity: the cutoff
    // lands on this instant, so "older than" takes the whole desk (MEASURED 12/12
    // against the real database) and "newer than" takes nothing (0/12). The server
    // refuses both structurally — this is what stops the spinner offering the first
    // of them as the value one keystroke below the default.
    //
    // The MAX is deliberately left at the registry's cap rather than lowered by
    // one. The cap is what the `int` DATEADD and the `datetime2` range can survive,
    // it is the number the "between 0 and N" message quotes, and a browser that
    // clamped to N-1 would disagree with that message. Typing the cap is refused by
    // the server with an explanation, which is the right place for a rule about
    // meaning rather than about range.
    return (
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <input
          type="number"
          min="1"
          max={control.max ?? undefined}
          step="1"
          className="field-input min-w-0 flex-1 py-1 text-sm"
          value={typeof row.value === 'string' || typeof row.value === 'number' ? row.value : ''}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="shrink-0 text-xs text-ink-secondary">hours ago</span>
      </span>
    );
  }

  if (control.kind === 'keyed_value') {
    const value = row.value && typeof row.value === 'object' && !Array.isArray(row.value)
      ? row.value : {};
    const key = typeof value.key === 'string' ? value.key : '';
    const compared = typeof value.value === 'string' || typeof value.value === 'number'
      ? String(value.value) : '';
    return (
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <select
          className="field-input min-w-0 flex-1 py-1 text-sm"
          value={key}
          disabled={status === 'loading'}
          onChange={(event) => onChange(
            control.arity === 'none'
              ? { key: event.target.value }
              : { key: event.target.value, value: compared },
          )}
        >
          <option value="">Choose a field…</option>
          {withStoredOptions(fetched, [key]).map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        {control.arity !== 'none' && (
          <input
            type="text"
            className="field-input min-w-0 flex-1 py-1 text-sm"
            placeholder="value stored on the ticket"
            value={compared}
            onChange={(event) => onChange({ key, value: event.target.value })}
          />
        )}
      </span>
    );
  }

  if (control.kind === 'name_list') {
    return (
      <TagsInput
        listId={listId}
        initial={asList(row.value, control).join(' ')}
        options={fetched}
        onChange={onChange}
      />
    );
  }

  const selected = asList(row.value, control);
  const options = withStoredOptions(control.kind === 'enum' ? control.options : fetched, selected);

  if (control.arity === 'set') {
    return (
      <span className="min-w-0 flex-1">
        <CheckboxList
          id={listId}
          options={options}
          selected={selected}
          disabled={status === 'loading'}
          onToggle={(value) => onChange(
            selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
          )}
        />
      </span>
    );
  }

  const single = typeof row.value === 'string' || typeof row.value === 'number'
    ? String(row.value) : '';
  return (
    <select
      className="field-input min-w-0 flex-1 py-1 text-sm"
      value={single}
      disabled={status === 'loading'}
      onChange={(event) => onChange(event.target.value)}
    >
      {/* `""` is not "unset" on an id field — it is `IS NULL`, which is what
          "Unassigned" means and what migration 012's own seeded view stores. On a
          closed enum it really is unset, and `conditionProblems` blocks Save. */}
      <option value="">{control.nobody ? 'Nobody / none' : 'Choose…'}</option>
      {control.sentinels.map((sentinel) => (
        <option key={sentinel} value={sentinel}>
          {`${humanize(sentinel)} (whoever is looking)`}
        </option>
      ))}
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function LockedRow({ row, onRemove }) {
  return (
    <li className="rounded border border-line-strong bg-sunken p-2">
      <div className="flex items-start justify-between gap-2">
        <code className="font-mono text-xs break-all text-ink">{JSON.stringify(row.raw)}</code>
        <button type="button" className="btn-ghost shrink-0 px-2 py-0.5 text-xs" onClick={onRemove}>
          Remove
        </button>
      </div>
      <p className="mt-1 text-xs text-ink-muted">
        {row.kind === 'locked'
          ? 'This filter works, but the console has no picker that could show its value '
            + 'safely. It is kept exactly as it is; changing it needs the JSON view.'
          : 'This desk has no such filter, so the whole view currently matches NOTHING. '
            + 'It is kept exactly as it is. Fix or remove it to make the view work again.'}
      </p>
    </li>
  );
}

/**
 * TWO THINGS THIS BUILDER SAYS ARE TRUE OF A SAVED VIEW AND NOT OF A REPORT, so the
 * screen that is not a view can override them. Both are props with the view's answer as
 * the default, so the admin console is unchanged.
 *
 * `/reports/builder` reuses this builder for a report's filters, which are the same
 * object compiled by the same `compileConditions` — but the consequences differ:
 *
 *   An EMPTY condition set makes a view a named queue showing the entire desk, which is
 *   why `conditionProblems` refuses it. A report with no filters is the ordinary case,
 *   bounded by its window, so the report builder passes `allowEmpty`.
 *
 *   An UNKNOWN FIELD makes a view compile to MATCH_NONE and render an empty queue,
 *   because `compileViewFor` catches the throw. On a report nothing catches it:
 *   `validateDefinition` turns it into a 422 and no rows render at all. Telling a lead
 *   that their report "matches no tickets" would be the class of claim this project has
 *   had to correct five times across the last two branches.
 */
const BROKEN_VIEW_MESSAGE = 'One of the rows below names a filter this desk does not have. '
  + 'Until it is fixed or removed, this view matches no tickets at all.';

export default function ConditionBuilder({
  id, value, vocabulary, onChange, onShowJson,
  allowEmpty = false, brokenMessage = BROKEN_VIEW_MESSAGE,
}) {
  const seq = useRef(0);
  const fields = authorableFields(vocabulary);
  // Derived from the committed value on every render rather than held as state,
  // so the rows an admin sees and the JSON that would be saved cannot drift apart
  // — and so `conditionProblems` reads the same thing DescriptorForm blocks Save
  // on. Row ids are positional (`all-0`, `all-1`), so an edit keeps its identity
  // and only a removal renumbers.
  const draft = conditionsToDraft(value, vocabulary);

  const needed = useMemo(() => {
    const seen = new Map();
    for (const row of draft.rows) {
      if (row.kind !== 'known') continue;
      const control = valueControlFor(row.field, row.operator, vocabulary);
      if (control.source && !seen.has(control.source)) {
        seen.set(control.source, { source: control.source, includeInactive: control.includeInactive });
      }
    }
    return [...seen.values()].sort((a, b) => a.source.localeCompare(b.source));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(draft.rows.map((r) => [r.kind, r.field, r.operator])), vocabulary]);

  const [sources, retry] = useValueSources(needed);
  // `conditionProblems` reports an empty set as a problem because that is what it is for
  // a view. With `allowEmpty` the whole check is skipped for an empty draft rather than
  // the message being filtered out of the list — a filter on the TEXT would silently stop
  // working the day that sentence is reworded.
  const problems = allowEmpty && conditionCount(draft) === 0
    ? []
    : conditionProblems(draft, vocabulary);
  const failed = Object.entries(sources).filter(([, s]) => s.status === 'failed').map(([s]) => s);
  const commit = (next) => onChange(draftToConditions(next));
  const broken = draft.rows.some((row) => row.kind === 'unknown');

  return (
    <div id={id} className="rounded-lg border border-line bg-surface p-3">
      {broken && (
        <p className="mb-3 rounded border border-line-strong bg-sunken p-2 text-xs text-ink">
          {brokenMessage}
        </p>
      )}

      {draft.groups.map((group) => {
        const rows = draft.rows.filter((row) => row.group === group);
        return (
          <section key={group} className="mb-3">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-secondary">
              {groupHeading(group)}
            </h3>
            {rows.length === 0 && (
              <p className="text-xs text-ink-muted">Nothing here yet.</p>
            )}
            <ul className="grid gap-2">
              {rows.map((row) => {
                if (row.kind !== 'known') {
                  return (
                    <LockedRow
                      key={row.id}
                      row={row}
                      onRemove={() => commit(removeRow(draft, row.id))}
                    />
                  );
                }
                const spec = specFor(row.field, vocabulary);
                const control = valueControlFor(row.field, row.operator, vocabulary);
                return (
                  <li key={row.id} className="flex flex-wrap items-center gap-2">
                    <select
                      className="field-input min-w-0 flex-1 py-1 text-sm"
                      value={row.field}
                      onChange={(event) => commit(replaceRow(
                        draft, row.id, refieldRow(row, event.target.value, vocabulary),
                      ))}
                    >
                      {fields.map((entry) => (
                        <option key={entry.field} value={entry.field}>
                          {fieldLabel(entry.field)}
                        </option>
                      ))}
                    </select>
                    <select
                      className="field-input min-w-0 flex-1 py-1 text-sm"
                      value={row.operator}
                      onChange={(event) => commit(replaceRow(
                        draft, row.id, reoperateRow(row, event.target.value, vocabulary),
                      ))}
                    >
                      {(spec?.operators ?? []).map((operator) => (
                        <option key={operator} value={operator}>{humanize(operator)}</option>
                      ))}
                    </select>
                    <ValueControl
                      key={`${row.id}-${row.field}-${row.operator}`}
                      row={row}
                      control={control}
                      sources={sources}
                      listId={`${id}-${row.id}-list`}
                      onChange={(value) => commit(replaceRow(
                        draft, row.id, setRowValue(row, value),
                      ))}
                    />
                    <button
                      type="button"
                      className="btn-ghost shrink-0 px-2 py-0.5 text-xs"
                      onClick={() => commit(removeRow(draft, row.id))}
                    >
                      Remove
                    </button>
                    {control.ordered && (
                      <p className="w-full text-xs text-ink-muted">
                        Compared in workflow order, not alphabetically.
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              className="btn-ghost mt-2 px-2 py-0.5 text-xs ring-1 ring-line-strong disabled:opacity-50"
              disabled={!canAddConditionRow(draft, vocabulary) || fields.length === 0}
              onClick={() => {
                seq.current += 1;
                commit(addRow(draft, newConditionRow(vocabulary, group, seq.current)));
              }}
            >
              + Add condition
            </button>
          </section>
        );
      })}

      <div className="flex flex-wrap items-center gap-3">
        {!draft.groups.includes('any') && (
          <button
            type="button"
            className="btn-ghost px-2 py-0.5 text-xs ring-1 ring-line-strong"
            onClick={() => {
              seq.current += 1;
              commit(addRow(draft, newConditionRow(vocabulary, 'any', seq.current)));
            }}
          >
            + Add an &ldquo;any of these&rdquo; group
          </button>
        )}
        <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={onShowJson}>
          Edit as JSON
        </button>
        {!canAddConditionRow(draft, vocabulary) && (
          <span className="text-xs text-ink-muted">
            {maxConditionRows(vocabulary)} is the most a view can carry.
          </span>
        )}
      </div>

      {failed.length > 0 && (
        <p className="mt-2 text-xs text-ink-secondary">
          Couldn&rsquo;t load the {failed.join(' and ')} list. The values already saved are still
          shown.{' '}
          <button type="button" className="underline" onClick={retry}>Retry</button>
        </p>
      )}

      {problems.length > 0 && (
        <ul className="mt-2 grid gap-0.5">
          {problems.map((problem) => (
            <li key={problem.id ?? problem.message} className="text-xs text-red-700 dark:text-red-300">
              {problem.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
